import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FeatureHintAnsicht, FeatureHintInhalt } from "../../feature-hints/FeatureHintAnsicht";

/**
 * Die Neuigkeit so, wie der Kunde sie sieht — neben der Schreibmaske (KAN-299).
 *
 * Solange die Texte reiner Text waren, war die Maske selbst die Vorschau: Was dort stand,
 * stand auch beim Kunden. Seit der Text als Markdown gesetzt wird, tippt man Zeichen, die
 * der Kunde nicht sieht. Diese Flaeche schliesst die Luecke.
 *
 * Breite und Rahmen sind die des Neuigkeiten-Bereichs beim Kunden, nicht zufaellig:
 * Dieselben 410 Pixel brechen die Zeilen an denselben Stellen. Eine Vorschau in einer
 * anderen Breite wuerde eine Gestaltung zeigen, die es so nicht gibt.
 *
 * Ein Klick ins Leere schliesst sie, wie beim Kunden. Klicks auf Eingabefelder und
 * Knoepfe zaehlen nicht als "ins Leere": Wer weiterschreibt, waehrend die Vorschau offen
 * steht, soll sie nicht bei jedem Griff ins Textfeld verlieren.
 */
export const KundenVorschau = ({
  inhalt,
  datum,
  onClose,
}: {
  inhalt: FeatureHintInhalt;
  datum: string;
  onClose: () => void;
}) => {
  const feld = useRef<HTMLElement>(null);

  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent) => {
      if (ereignis.key === "Escape") onClose();
    };

    const beiKlick = (ereignis: MouseEvent) => {
      const ziel = ereignis.target as HTMLElement | null;
      if (!ziel) return;
      if (feld.current?.contains(ziel)) return;
      // Womit man arbeitet, schliesst nicht: Eingabefelder, Knoepfe, Beschriftungen - und
      // der Vorschau-Knopf selbst, der sonst zumachen und im selben Zug wieder oeffnen
      // wuerde.
      if (ziel.closest("input, textarea, select, button, label, a, [role=\"dialog\"]")) return;
      onClose();
    };

    document.addEventListener("keydown", beiTaste);
    document.addEventListener("mousedown", beiKlick);
    return () => {
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("mousedown", beiKlick);
    };
  }, [onClose]);

  return createPortal(
    <aside
      ref={feld}
      aria-label="Vorschau für den Kunden"
      // z-[110] wie beim Kunden: react-draft-wysiwyg legt seine Auswahlfelder auf 90 und
      // 100 und zeichnete darunter mitten durch den Text.
      className="fixed bottom-0 right-0 top-0 z-[110] flex w-[410px] max-w-[calc(100vw-82px)] flex-col border-l border-gray-200 bg-white shadow-[-4px_0_16px_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Vorschau</h2>
          <p className="mt-0.5 text-xs text-gray-500">So sieht der Kunde den Eintrag.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Vorschau schließen"
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-50"
        >
          <X size={18} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {/* Dasselbe Bauteil, das der Kunde bekommt. Die Knoepfe fuehren hier nirgendwo hin:
            Aus der Schreibmaske heraus in die Kundenansicht zu springen waere ein Verlust
            des Entwurfs. */}
        <FeatureHintAnsicht inhalt={inhalt} ueberschrift={datum} onInternal={() => undefined} />
      </div>
    </aside>,
    document.body
  );
};

export default KundenVorschau;
